import type { TechlineCommentEntity } from '../entities';

import { StsBackendOperation } from '../../../sts-backend-operation';
import { sortCommentsByDate, toTechlineCommentEntity } from '../helpers';

/** Parameters for fetching comments on a techline case. */
export type FetchCommentsParams = {
    /** The case number to fetch comments for. */
    caseNumber: string;
};

/**
 * Fetch all comments for a techline case and return them as domain entities sorted by
 * creation date (newest first). Maps raw backend DTOs through {@link toTechlineCommentEntity}
 * and delegates sorting to {@link sortCommentsByDate}.
 */
export class FetchComments extends StsBackendOperation {
    /**
     * Fetch, map, and sort comments for a case.
     *
     * @param params - the case number to fetch comments for
     * @returns comment entities sorted by creation date descending
     */
    async exec(params: FetchCommentsParams): Promise<TechlineCommentEntity[]> {
        const { caseNumber } = params;
        // Fetch raw comment records from the backend
        const response = await this.client.getComments({ caseNumber });

        if (!response.success) {
            return [];
        }

        const results = response.data.results;

        if (!results?.length) {
            return [];
        }

        // Map DTOs to domain entities and sort newest-first
        const entities = results.map(toTechlineCommentEntity);
        return sortCommentsByDate(entities);
    }
}
